import { Log, send } from '@openticket/lib-log';
import { StringMessage } from '@openticket/lib-order';
import type { CookieStorageContract } from './types';

export const localStorageTestKey = '@openticket:test-local-storage' as const;
export const localStoragePrefix = '@openticket:cookieprefs' as const;

export class CookieLocalStorage implements CookieStorageContract {

    enabled: boolean;

    constructor() {
        this.enabled = hasLocalStorage();

        if (!this.enabled) {
            send(
                new StringMessage(
                    'error.cookies.disabled',
                    'Cookie preferences storage (localStorage) disabled',
                ),
                Log.Info,
            );
        }
    }

    get(key: string): boolean | null {
        if (!this.enabled) {
            return null;
        }

        try {
            const value = localStorage.getItem(this.getKey(key));

            if (typeof value === 'string') {
                return value === '1';
            }
        } catch (e) {
            send(
                new StringMessage(
                    'error.cookies.get.failed',
                    'Failed to get cookie preference.',
                    { key, error: e },
                ),
                Log.Error,
            );
        }

        return null;
    }

    set(key: string, value: boolean | null): void {
        if (!this.enabled) {
            return;
        }

        try {
            if (typeof value === 'boolean') {
                localStorage.setItem(this.getKey(key), value ? '1' : '0');
            } else {
                localStorage.removeItem(this.getKey(key));
            }
        } catch (e) {
            send(
                new StringMessage(
                    'error.cookies.set.failed',
                    'Failed to set cookie preference.',
                    { key, value, error: e },
                ),
                Log.Error,
            );
        }
    }

    getKey(key: string): string {
        return `${localStoragePrefix}:${key}`;
    }

}

export function hasLocalStorage(): boolean {
    try {
        if (typeof localStorage !== 'undefined') {
            try {
                localStorage.setItem(localStorageTestKey, localStorageTestKey);

                if (
                    localStorage.getItem(localStorageTestKey)
                    === localStorageTestKey
                ) {
                    return true;
                }
            } finally {
                // Always cleanup this value - If this fails, the wrapping try will catch it.
                localStorage.removeItem(localStorageTestKey);
            }
        }

        return false;
    } catch (e) {
        return false;
    }
}
